import { useEffect, useRef, useState } from "react";
import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { refreshEquipmentEtaMs } from "~/utils/equipmentEta";

interface Props {
  etaMs: number;
  onRefreshed: (etaMs: number) => void;
  size?: number;
}

/**
 * Equipment-ETA refresh control: a refresh icon that spins for 2-3 seconds, then
 * re-rolls the ETA (today only: 20% earlier, 20% later, 60% unchanged) and calls
 * onRefreshed with the new ETA. Shared by the intake queue and the episode dialog.
 */
const EquipmentEtaRefreshButton: React.FC<Props> = ({ etaMs, onRefreshed, size = 16 }) => {
  const [refreshing, setRefreshing] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleClick = () => {
    if (refreshing) {
      return;
    }
    setRefreshing(true);
    const delay = 2000 + Math.random() * 1000; // 2-3 seconds
    timerRef.current = setTimeout(() => {
      onRefreshed(refreshEquipmentEtaMs(etaMs));
      setRefreshing(false);
      timerRef.current = null;
    }, delay);
  };

  if (refreshing) {
    return <CircularProgress size={size} sx={{ ml: 0.5 }} />;
  }

  return (
    <Tooltip title="Refresh ETA">
      <IconButton size="small" onClick={handleClick}>
        <RefreshIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

export default EquipmentEtaRefreshButton;
